'use client';

import { useEffect, useState } from 'react';
import { interpolate, useTranslation } from '../../lib/i18n';
import type { BrainStatus, BrainTypingParticipant } from '../../lib/types';

interface Props {
  typing: readonly BrainTypingParticipant[];
  viewerId: number;
  shared: boolean;
  turnWriter: { id: number; name: string } | null | undefined;
  programStatus: BrainStatus['programStatus'];
}

export function SharedConversationStatus({ typing, viewerId, shared, turnWriter, programStatus }: Props) {
  const { t } = useTranslation();
  const [revision, setRevision] = useState(0);
  const now = Date.now();
  useEffect(() => {
    const current = Date.now();
    const next = Math.min(...typing.filter((p) => p.expiresAt > now).map((p) => p.expiresAt));
    if (!Number.isFinite(next)) return;
    const timeout = setTimeout(() => setRevision((value) => value + 1), Math.max(0, next - current));
    return () => clearTimeout(timeout);
  }, [typing, revision, now]);
  const names = new Map<number, string>();
  if (shared) for (const person of typing) {
    if (person.userId !== viewerId && person.expiresAt > now) names.set(person.userId, person.name);
  }
  // Stable account order prevents heartbeat-only snapshots from re-announcing the same set.
  const list = [...names.entries()].sort(([a], [b]) => a - b).map(([, name]) => name);
  const message = list.length === 1
    ? interpolate(t.brainChat.ux.typingOne, { name: list[0] })
    : list.length > 1 ? interpolate(t.brainChat.ux.typingMany, { names: list.join(', ') }) : '';
  const writer = shared && turnWriter && (programStatus.state === 'working' || programStatus.state === 'blocked')
    ? interpolate(t.brainChat.ux.answeringUser, { name: turnWriter.name }) : '';
  // One quiet metadata line, never a card or a second row; the full text stays in the title when it is cut.
  const line = [message, writer].filter(Boolean).join(' · ');
  return <div aria-live="polite" title={line || undefined} className="min-w-0 truncate text-meta text-muted-foreground">{line}</div>;
}
